import { COMPASS_PLACES_DEFAULT, DEFAULT_FRAME_LIMIT, type FrameLimit, type InterfaceStep, type OfferedInvitation, type RenderDivisor } from "odyssey-shared";
import { defaultLevel, levelIds, type LevelId, type VolumeLevel } from "../audio/audio-settings.ts";
import type { ChatSizeChoice } from "../chat/size.ts";
import { CONTROL_ROWS, MOVE_SETS, type ActionId } from "../controls/actions.ts";
import { DEFAULT_BINDINGS, chooseKey, resolveBindings, type Bindings } from "../controls/bindings.ts";
import type { LayoutChoice } from "../controls/layout.ts";
import { DEFAULT_RUN_MODE, type RunMode } from "../run/intent.ts";
import type { ChatTextChoice } from "../chat/text-size.ts";
import { DEFAULT_NAMEPLATE_CHOICES, type BarPagingChoice, type OthersNameplates, type OwnNameplate, type ZoomStep } from "../settings.ts";
import { type ReducedMotionChoice } from "../reduced-motion.ts";

/**
 * Settings' pending changes (P1.9-T44; ADR-061's amendment "Settings in sections, and nothing applies until
 * Apply"), as pure data: a draft holds the values in force when Settings opened and the values the rows show,
 * and a row is changed while the two differ. Nothing here reaches the page; `settings-page.ts` reads the values
 * in force and puts applied ones in force. Every function returns a new draft, or the same one when nothing
 * changes.
 */

/**
 * A section of Settings, by the design's names.
 */
export type SettingsSection = "display" | "interface" | "audio" | "controls" | "access" | "social" | "account";

/**
 * The sections in the order the section list shows them, the design's.
 */
export const SECTION_ORDER: readonly SettingsSection[] = ["display", "interface", "audio", "controls", "access", "social", "account"];

/**
 * Every setting's value: the render resolution's divisor in force, the frame limit, the world zoom's step, whether the performance panel shows, the two Nameplates choices,
 * whether the player card's health value, other players' idle and AFK marks, other players' emoji, the minimap's coordinates line and the ability bar's keys show, how many known places the compass band points to (0 to 12), the Ability bar choice (All 10 or Five at a time), the chat panel's size (one of
 * Settings' three, or `custom` for a size the panel's grip left between them, which no choice shows and Apply never
 * sets), the reduced motion choice, the chat text size (Normal, Large, Larger or Largest), the Interface size step (-2 to 2 from the automatic UI scale), whether sound stops while the window is in the background, Master's and each
 * channel's level, the Run setting (ADR-116 decision 11), Lock the ability bar (ADR-121 decision 3.2), who may invite the character to a party (ADR-101 decision 6), and each action's key (P1.9-T50), whose pending table may
 * hold one key twice until Apply (a clash).
 */
export interface SettingsValues {
  readonly renderDivisor: RenderDivisor;
  readonly frameLimit: FrameLimit;
  readonly zoom: ZoomStep;
  readonly performance: boolean;
  readonly othersNameplates: OthersNameplates;
  readonly ownNameplate: OwnNameplate;
  readonly healthNumbers: boolean;
  readonly idleMarks: boolean;
  readonly emotesOthers: boolean;
  readonly coordinates: boolean;
  readonly compassPlaces: number;
  readonly autoLoot: boolean;
  readonly barKeys: boolean;
  readonly barPaging: BarPagingChoice;
  readonly chatSize: ChatSizeChoice | "custom";
  readonly reducedMotion: ReducedMotionChoice;
  readonly chatText: ChatTextChoice;
  readonly interfaceStep: InterfaceStep;
  readonly backgroundMute: boolean;
  readonly whisperBackground: boolean;
  readonly runMode: RunMode;
  readonly barLocked: boolean;
  readonly keyLayout: LayoutChoice;
  readonly partyInvites: OfferedInvitation;
  readonly levels: Readonly<Record<LevelId, VolumeLevel>>;
  readonly keys: Bindings;
}

/**
 * A setting that is one plain value.
 */
export type PlainSetting = Exclude<keyof SettingsValues, "levels" | "keys">;

/**
 * A level's setting, named by Master's or its channel's id.
 */
export type LevelSetting = `level.${LevelId}`;

/**
 * An action's key as a setting, named by the action's id.
 */
export type KeySetting = `key.${ActionId}`;

/**
 * One row's setting.
 */
export type SettingId = PlainSetting | LevelSetting | KeySetting;

/**
 * The values in force and the values the rows show.
 */
export interface SettingsDraft {
  readonly applied: SettingsValues;
  readonly pending: SettingsValues;
}

/**
 * The setting of a level.
 *
 * @param id Master or a channel.
 * @returns The setting's id.
 */
export function levelSetting(id: LevelId): LevelSetting {
  return `level.${id}`;
}

/**
 * The setting of an action's key.
 *
 * @param action The action.
 * @returns The setting's id.
 */
export function keySetting(action: ActionId): KeySetting {
  return `key.${action}`;
}

/**
 * The plain settings of each section, in row order. Audio's levels come before its background choice, and Controls'
 * Run stands among its keys where the pane lists it (`CONTROL_ROWS`).
 */
const SECTION_PLAIN: Readonly<Record<SettingsSection, readonly PlainSetting[]>> = {
  display: ["renderDivisor", "frameLimit", "zoom", "performance"],
  interface: ["othersNameplates", "ownNameplate", "healthNumbers", "idleMarks", "emotesOthers", "coordinates", "compassPlaces", "autoLoot", "barKeys", "barPaging", "chatSize"],
  audio: ["backgroundMute", "whisperBackground"],
  controls: ["keyLayout", "runMode", "barLocked"],
  access: ["interfaceStep", "chatText", "reducedMotion"],
  social: ["partyInvites"],
  account: [],
};

/**
 * A section's settings, in row order.
 *
 * @param section The section.
 * @returns The settings' ids; none for a section that holds no setting.
 */
export function sectionSettings(section: SettingsSection): readonly SettingId[] {
  if (section === "controls") {
    return ["keyLayout", ...CONTROL_ROWS.flatMap((row): SettingId[] => (row.kind === "move" ? MOVE_SETS[row.set].map(keySetting) : row.kind === "action" ? [keySetting(row.id)] : row.kind === "run" ? ["runMode", keySetting("run")] : row.kind === "barLock" ? ["barLocked"] : []))];
  }
  return section === "audio" ? [...levelIds().map(levelSetting), ...SECTION_PLAIN.audio] : SECTION_PLAIN[section];
}

/**
 * The value every setting has before a viewer chooses: each one's own default.
 */
export const DEFAULT_SETTINGS: SettingsValues = Object.freeze({
  renderDivisor: 1,
  frameLimit: DEFAULT_FRAME_LIMIT,
  zoom: 0,
  performance: false,
  othersNameplates: DEFAULT_NAMEPLATE_CHOICES.others,
  ownNameplate: DEFAULT_NAMEPLATE_CHOICES.own,
  healthNumbers: true,
  idleMarks: true,
  emotesOthers: true,
  coordinates: true,
  compassPlaces: COMPASS_PLACES_DEFAULT,
  autoLoot: false,
  barKeys: false,
  barPaging: "all",
  chatSize: "medium",
  reducedMotion: "system",
  chatText: "normal",
  interfaceStep: 0,
  backgroundMute: true,
  whisperBackground: true,
  runMode: DEFAULT_RUN_MODE,
  barLocked: false,
  keyLayout: "auto",
  partyInvites: "map",
  levels: Object.freeze(Object.fromEntries(levelIds().map((id) => [id, defaultLevel(id)])) as Record<LevelId, VolumeLevel>),
  keys: DEFAULT_BINDINGS,
});

/**
 * Whether two levels are the same volume and mute.
 *
 * @param a One level.
 * @param b The other.
 * @returns True when both parts match.
 */
function sameLevel(a: VolumeLevel, b: VolumeLevel): boolean {
  return a.volume === b.volume && a.muted === b.muted;
}

/**
 * The level a level's setting names, or null for a plain setting.
 *
 * @param id The setting.
 * @returns Master's or the channel's id, or null.
 */
function levelOf(id: SettingId): LevelId | null {
  return id.startsWith("level.") ? (id.slice("level.".length) as LevelId) : null;
}

/**
 * The action a key's setting names, or null for any other setting.
 *
 * @param id The setting.
 * @returns The action, or null.
 */
export function actionOf(id: SettingId): ActionId | null {
  return id.startsWith("key.") ? (id.slice("key.".length) as ActionId) : null;
}

/**
 * Whether one setting has the same value in two sets of values.
 *
 * @param a One set.
 * @param b The other.
 * @param id The setting.
 * @returns True when the values match.
 */
function sameSetting(a: SettingsValues, b: SettingsValues, id: SettingId): boolean {
  const action = actionOf(id);
  if (action !== null) {
    return a.keys[action] === b.keys[action];
  }
  const level = levelOf(id);
  return level === null ? a[id as PlainSetting] === b[id as PlainSetting] : sameLevel(a.levels[level], b.levels[level]);
}

/**
 * Opens a draft on the values in force, with nothing changed.
 *
 * @param applied The values in force.
 * @returns The draft.
 * @example
 * changedCount(setValue(openDraft(DEFAULT_SETTINGS), "zoom", 1));
 * // 1
 */
export function openDraft(applied: SettingsValues): SettingsDraft {
  return { applied, pending: applied };
}

/**
 * Sets a plain setting's pending value.
 *
 * @param draft The draft.
 * @param id The setting.
 * @param value Its new value.
 * @returns The draft with the value pending, or the same draft when it already is.
 */
export function setValue<K extends PlainSetting>(draft: SettingsDraft, id: K, value: SettingsValues[K]): SettingsDraft {
  return draft.pending[id] === value ? draft : { ...draft, pending: { ...draft.pending, [id]: value } };
}

/**
 * Sets a level's pending value.
 *
 * @param draft The draft.
 * @param id Master or a channel.
 * @param level Its new level.
 * @returns The draft with the level pending, or the same draft when it already is.
 */
export function setLevel(draft: SettingsDraft, id: LevelId, level: VolumeLevel): SettingsDraft {
  return sameLevel(draft.pending.levels[id], level) ? draft : { ...draft, pending: { ...draft.pending, levels: { ...draft.pending.levels, [id]: level } } };
}

/**
 * Chooses a key for an action as a pending change (`chooseKey`): a key another action holds pends twice until Apply.
 *
 * @param draft The draft.
 * @param action The action.
 * @param code The physical key.
 * @returns The draft with the key pending, or the same draft when it already is.
 */
export function setKey(draft: SettingsDraft, action: ActionId, code: string): SettingsDraft {
  const keys = chooseKey(draft.applied.keys, draft.pending.keys, action, code);
  return keys === draft.pending.keys ? draft : { ...draft, pending: { ...draft.pending, keys } };
}

/**
 * Sets one action's pending key to its default.
 *
 * @param draft The draft.
 * @param action The action.
 * @returns The draft with the default pending, or the same draft when it already is.
 */
export function resetKey(draft: SettingsDraft, action: ActionId): SettingsDraft {
  return setKey(draft, action, DEFAULT_BINDINGS[action]);
}

/**
 * Takes a value the page put in force by itself while Settings is open, as the render resolution falls back when
 * the window stops offering it: the value in force moves, and the pending one moves with it unless the row was
 * changed and its pending value may stay.
 *
 * @param draft The draft.
 * @param id The setting.
 * @param value The value now in force.
 * @param keep Whether a changed row keeps its pending value.
 * @returns The draft on the new value in force, or the same draft when nothing moved.
 */
export function rebaseValue<K extends PlainSetting>(draft: SettingsDraft, id: K, value: SettingsValues[K], keep: boolean): SettingsDraft {
  const pending = keep && draft.pending[id] !== draft.applied[id] ? draft.pending[id] : value;
  if (draft.applied[id] === value && draft.pending[id] === pending) {
    return draft;
  }
  const applied = { ...draft.applied, [id]: value };
  return { applied, pending: draft.applied === draft.pending ? applied : { ...draft.pending, [id]: pending } };
}

/**
 * Whether a row is changed: its pending value differs from the one in force.
 *
 * @param draft The draft.
 * @param id The row's setting.
 * @returns True when it carries the changed mark.
 */
export function isChanged(draft: SettingsDraft, id: SettingId): boolean {
  return !sameSetting(draft.applied, draft.pending, id);
}

/**
 * The changed rows, in section order and then row order.
 *
 * @param draft The draft.
 * @returns The settings whose pending value differs from the one in force.
 */
export function changedSettings(draft: SettingsDraft): readonly SettingId[] {
  return draft.applied === draft.pending ? [] : SECTION_ORDER.flatMap((section) => sectionSettings(section).filter((id) => isChanged(draft, id)));
}

/**
 * How many rows are changed.
 *
 * @param draft The draft.
 * @returns The count.
 */
export function changedCount(draft: SettingsDraft): number {
  return changedSettings(draft).length;
}

/**
 * Whether every pending row of a section is at its default, as it is for a section with no setting.
 *
 * @param draft The draft.
 * @param section The section.
 * @returns True when Reset this tab would change nothing.
 */
export function sectionAtDefaults(draft: SettingsDraft, section: SettingsSection): boolean {
  return sectionSettings(section).every((id) => sameSetting(draft.pending, DEFAULT_SETTINGS, id));
}

/**
 * Reset this tab: sets a section's rows to their defaults as pending changes, leaving every other section and
 * the values in force.
 *
 * @param draft The draft.
 * @param section The section.
 * @returns The draft with the section's defaults pending, or the same draft when they already are.
 */
export function resetSection(draft: SettingsDraft, section: SettingsSection): SettingsDraft {
  if (sectionAtDefaults(draft, section)) {
    return draft;
  }
  const plain = Object.fromEntries(SECTION_PLAIN[section].map((id) => [id, DEFAULT_SETTINGS[id]]));
  return { ...draft, pending: { ...draft.pending, ...plain, levels: section === "audio" ? DEFAULT_SETTINGS.levels : draft.pending.levels, keys: section === "controls" ? DEFAULT_SETTINGS.keys : draft.pending.keys } };
}

/**
 * Apply: the pending values become the ones in force, the keys by Apply's rule for them (`resolveBindings`), which
 * swaps a clash.
 *
 * @param draft The draft.
 * @returns A draft with nothing changed, on the pending values.
 */
export function applyDraft(draft: SettingsDraft): SettingsDraft {
  const keys = resolveBindings(draft.applied.keys, draft.pending.keys);
  return openDraft(keys === draft.pending.keys ? draft.pending : { ...draft.pending, keys });
}

/**
 * Discard: the pending values are dropped.
 *
 * @param draft The draft.
 * @returns A draft with nothing changed, on the values in force.
 */
export function discardDraft(draft: SettingsDraft): SettingsDraft {
  return openDraft(draft.applied);
}
